/**
 * 🔒 Decision 7540 (T3109): Test and Run may share one bucket and one Mirror,
 * so every object key the application writes begins with its environment's
 * prefix, read from the environment kind. A key with no prefix was written
 * before the decision: Run owns it on the shared bucket, and a developer's
 * machine owns it on its own store. An environment reads any key its database
 * names; it writes, mirrors, prunes and deletes only keys it owns.
 *
 * These are the pure forms, each handed the prefix; the API's
 * `storage/environment-key.ts` holds the server's own prefix and reads them.
 */
export const ENVIRONMENT_KEY_PREFIXES = { production: 'run/', test: 'test/', development: 'dev/' } as const;

export type EnvironmentKind = keyof typeof ENVIRONMENT_KEY_PREFIXES;
export type EnvironmentKeyPrefix = (typeof ENVIRONMENT_KEY_PREFIXES)[EnvironmentKind];

const PREFIXES: readonly EnvironmentKeyPrefix[] = Object.values(ENVIRONMENT_KEY_PREFIXES);

/** The environments that own the keys written before the prefix: Run on the shared bucket, and a developer's own store. */
const HOLDS_UNPREFIXED: readonly EnvironmentKeyPrefix[] = [ENVIRONMENT_KEY_PREFIXES.production, ENVIRONMENT_KEY_PREFIXES.development];

/** 🔒 What an object written beside another environment's key goes under, after the writer's own prefix. */
export const FOREIGN_KEY_INFIX = 'foreign/';

/** A key under any environment's `foreign/` folder, which only `keyBeside` writes. */
const FOREIGN_BESIDE = new RegExp(`^(?:${PREFIXES.map((prefix) => prefix.replace('/', '\\/')).join('|')})${FOREIGN_KEY_INFIX.replace('/', '\\/')}`);

/** The prefix an environment kind writes under. */
export function environmentKeyPrefix(kind: EnvironmentKind): EnvironmentKeyPrefix {
  return ENVIRONMENT_KEY_PREFIXES[kind];
}

/** The environment prefix a key begins with, or null for a key written before decision 7540. */
export function keyPrefixOf(key: string): EnvironmentKeyPrefix | null {
  return PREFIXES.find((prefix) => key.startsWith(prefix)) ?? null;
}

/** The key with its environment prefix taken off, or the key itself where it carries none. */
export function unprefixedKey(key: string): string {
  const prefix = keyPrefixOf(key);
  return prefix ? key.slice(prefix.length) : key;
}

/** Whether an environment owns the keys written before the prefix. */
export function holdsUnprefixedKeys(prefix: EnvironmentKeyPrefix): boolean {
  return HOLDS_UNPREFIXED.includes(prefix);
}

/** The key a new object is written under: the environment's prefix, then the key. */
export function prefixedKey(prefix: EnvironmentKeyPrefix, key: string): string {
  return `${prefix}${key}`;
}

/** Whether the environment writing under this prefix owns the key: its own prefix, or no prefix where it holds the older keys. */
export function ownsKeyUnder(prefix: EnvironmentKeyPrefix, key: string): boolean {
  const held = keyPrefixOf(key);
  return held === null ? holdsUnprefixedKeys(prefix) : held === prefix;
}

/**
 * The key an object stands at beside another, a thumbnail or a sealed copy:
 * beside it where the environment owns the object's key, else under the
 * environment's own prefix and `foreign/`, so nothing is written under another
 * environment's key.
 * 🔒 A key already under a `foreign/` folder, such as Test's sealed copy of
 * Run's object, takes its beside-object under `foreign/` once more, never in
 * place, so `besideObjectKey` strips exactly the one folder this added and the
 * two are inverses for every key shape (review of T3109).
 */
export function keyBeside(prefix: EnvironmentKeyPrefix, objectKey: string, suffix: string): string {
  return ownsKeyUnder(prefix, objectKey) && !FOREIGN_BESIDE.test(objectKey) ? `${objectKey}${suffix}` : `${prefix}${FOREIGN_KEY_INFIX}${objectKey}${suffix}`;
}

/** The object a key beside it names once the suffix is off: a key written under `foreign/` reads back as the key it stands beside, one folder off. */
export function besideObjectKey(key: string): string {
  return key.replace(FOREIGN_BESIDE, '');
}
